import { useEffect, useRef } from "react";
import type { SupportKind, SupportTicketDetail } from "@/api/client";
import { Composer } from "./Composer";
import { NewTicketForm } from "./NewTicketForm";
import { fmtMessageTime, kindLabel } from "./supportLogic";
import type { SupportMode } from "./supportStore";

// Правая часть окна: заголовок, переписка и поле ответа — или форма нового
// обращения, или «Выберите обращение или создайте новое».

export function paneHead(
  mode: SupportMode,
  creating: boolean,
  detail: SupportTicketDetail | null,
): string {
  if (creating) return "Новое обращение";
  if (detail) {
    return `${detail.title} · ${kindLabel(detail.kind)} · ${detail.is_open ? "открыто" : "закрыто"}`;
  }
  return mode === "admin" ? "Обращения пользователей" : "Поддержка";
}

export function TicketPane({
  mode,
  creating,
  detail,
  loading,
  failed,
  closing,
  onBack,
  onExit,
  onCreate,
  onCancelNew,
  onSend,
  onCloseTicket,
}: {
  mode: SupportMode;
  creating: boolean;
  detail: SupportTicketDetail | null;
  /** Обращение выбрано, но ещё не пришло. */
  loading: boolean;
  failed: boolean;
  closing: boolean;
  onBack: () => void;
  onExit: () => void;
  onCreate: (kind: SupportKind, title: string, text: string) => Promise<string | null>;
  onCancelNew: () => void;
  onSend: (text: string, image?: File) => Promise<string | null>;
  onCloseTicket: () => void;
}) {
  const admin = mode === "admin";
  const thread = useRef<HTMLDivElement>(null);
  const last = detail?.messages[detail.messages.length - 1]?.id;

  // Переписка читается с конца: при новом сообщении и при смене обращения —
  // вниз. Если человек листал выше, а пришёл опрос без новых реплик, не трогаем.
  useEffect(() => {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [detail?.id, last]);

  const sub = admin && detail && !creating ? `${detail.user_name}${detail.company ? ` · ${detail.company}` : ""}` : "";

  return (
    <section className="sup-conv" aria-label="Обращение">
      <div className="sup-conv-h">
        <button type="button" className="sup-x sup-back" aria-label="К списку" title="К списку" onClick={onBack}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3L5 8l5 5" />
          </svg>
        </button>
        <div className="sup-conv-name">
          <b>{paneHead(mode, creating, detail)}</b>
          {sub && <div className="sup-conv-sub">{sub}</div>}
        </div>
        {admin && !creating && detail?.is_open && (
          <button type="button" className="sup-danger" disabled={closing} onClick={onCloseTicket}>
            Закрыть обращение
          </button>
        )}
        <button type="button" className="sup-x" title="Закрыть" aria-label="Закрыть" onClick={onExit}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {creating && !admin ? (
        <NewTicketForm onCreate={onCreate} onCancel={onCancelNew} />
      ) : detail ? (
        <>
          <div className="sup-thread" ref={thread}>
            {detail.messages.map((m) => {
              // Свои реплики справа: у автора — его, у администратора — ответы поддержки.
              const out = admin ? m.from_staff : !m.from_staff;
              return (
                <div key={m.id} className={"sup-msg " + (out ? "sup-msg--out" : "sup-msg--in")}>
                  <div className="sup-bubble">
                    {m.image_url && (
                      <a href={m.image_url} target="_blank" rel="noreferrer">
                        <img className="sup-img" src={m.image_url} alt="Изображение" />
                      </a>
                    )}
                    {m.text}
                    <div className="sup-time">{fmtMessageTime(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {detail.is_open ? (
            <Composer
              key={detail.id}
              placeholder={admin ? "Ответить пользователю" : "Написать в поддержку"}
              onSend={onSend}
            />
          ) : (
            <div className="sup-closed">
              {admin
                ? "Обращение закрыто."
                : "Обращение закрыто, писать в него нельзя. Если вопрос остался, создайте новое обращение."}
            </div>
          )}
        </>
      ) : loading ? (
        <div className="sup-none">Загрузка…</div>
      ) : failed ? (
        <div className="sup-none">Не удалось открыть обращение</div>
      ) : (
        <div className="sup-none">Выберите обращение или создайте новое</div>
      )}
    </section>
  );
}
